import { useEffect, useRef, useState } from 'react'
import * as THREE from 'three'
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer'
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass'
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass'

interface PresentEffectProps {
  isVisible: boolean
  presentName: string
  presentImageUrl?: string
  customImageUrl?: string
  message?: string
  grantedByName: string
  onComplete: () => void
}

export default function PresentEffect({
  isVisible,
  presentName,
  presentImageUrl,
  customImageUrl,
  message,
  grantedByName,
  onComplete
}: PresentEffectProps) {
  const mountRef = useRef<HTMLDivElement>(null)
  const sceneRef = useRef<THREE.Scene | null>(null)
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null)
  const animationIdRef = useRef<number | null>(null)
  const timerRef = useRef<number>(0)
  const effectPhaseRef = useRef<'flowing' | 'contracting' | 'bursting' | 'complete'>('flowing')
  const [showImage, setShowImage] = useState(false)

  // Reset state when becoming visible
  useEffect(() => {
    if (isVisible) {
      effectPhaseRef.current = 'flowing'
      setShowImage(false)
      timerRef.current = 0
    }
  }, [isVisible])

  useEffect(() => {
    if (!isVisible || !mountRef.current) return

    // Clean up previous instance if it exists
    if (rendererRef.current) {
      rendererRef.current.dispose()
      if (mountRef.current && rendererRef.current.domElement.parentNode === mountRef.current) {
        mountRef.current.removeChild(rendererRef.current.domElement)
      }
      rendererRef.current = null
    }
    if (animationIdRef.current) {
      cancelAnimationFrame(animationIdRef.current)
      animationIdRef.current = null
    }

    let scene: THREE.Scene
    let camera: THREE.PerspectiveCamera
    let renderer: THREE.WebGLRenderer
    let composer: any // EffectComposer type from three/examples
    let particles: THREE.Points[] = []

    const particleGroups = 3
    const particlesPerGroup = 1200 // 増加させて豪華に

    // 再現性のある乱数生成器
    function mulberry32(seed: number) {
      return function() {
        let t = seed += 0x6D2B79F5
        t = Math.imul(t ^ (t >>> 15), t | 1)
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296
      }
    }
    const rand = mulberry32(123456)

    // Fibonacci Sphere配置（均等な球面配置）
    function fibonacciSphereTarget(i: number, N: number, radius: number): [number, number, number] {
      const t = i + 0.5
      const y = 1 - (t / N) * 2
      const r = Math.sqrt(1 - y * y)
      const phi = t * Math.PI * (3 - Math.sqrt(5))
      const x = Math.cos(phi) * r
      const z = Math.sin(phi) * r
      return [x * radius, y * radius, z * radius]
    }

    // カスタムシェーダー
    const vertexShader = `
      uniform float uPixelRatio;
      uniform float uPerspective;
      attribute float size;
      attribute float alpha;
      attribute vec3 customColor;
      varying vec3 vColor;
      varying float vAlpha;

      void main() {
        vColor = customColor;
        vAlpha = alpha;
        vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
        gl_Position = projectionMatrix * mvPosition;

        // 距離に応じたサイズ減衰
        float dist = -mvPosition.z;
        gl_PointSize = size * uPixelRatio * (uPerspective / max(dist, 0.0001));
      }
    `

    const fragmentShader = `
      varying vec3 vColor;
      varying float vAlpha;

      void main() {
        // ソフトな丸点（中心ほど不透明）
        vec2 p = gl_PointCoord - 0.5;
        float d = length(p);
        float soft = smoothstep(0.5, 0.0, d);

        // より強い発光効果
        float intensity = 1.0 - d * 2.0;
        vec3 core = vec3(1.0, 1.0, 1.0) * pow(max(intensity, 0.0), 4.0);
        vec3 glow = vColor * pow(max(intensity, 0.0), 2.0) * 3.0;

        // 中心を白く、外側を色付きに
        vec3 finalColor = mix(glow, core, pow(max(intensity, 0.0), 3.0));

        gl_FragColor = vec4(finalColor, vAlpha * soft);
      }
    `

    // 初期化
    const init = () => {
      scene = new THREE.Scene()
      scene.fog = new THREE.FogExp2(0x000000, 0.0015) // より強い奥行き感
      sceneRef.current = scene

      camera = new THREE.PerspectiveCamera(
        75,
        window.innerWidth / window.innerHeight,
        0.1,
        1000
      )
      camera.position.z = 30

      renderer = new THREE.WebGLRenderer({
        alpha: true,
        antialias: true,
        powerPreference: 'high-performance'
      })
      renderer.setSize(window.innerWidth, window.innerHeight)
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
      renderer.outputColorSpace = THREE.SRGBColorSpace
      renderer.toneMapping = THREE.ACESFilmicToneMapping
      renderer.toneMappingExposure = 1.0
      rendererRef.current = renderer

      mountRef.current?.appendChild(renderer.domElement)

      // ポストプロセシング設定（Bloom効果）
      composer = new EffectComposer(renderer)
      composer.addPass(new RenderPass(scene, camera))

      const bloomPass = new UnrealBloomPass(
        new THREE.Vector2(window.innerWidth, window.innerHeight),
        1.0,  // strength
        0.6,  // radius
        0.2   // threshold
      )
      composer.addPass(bloomPass)

      // パーティクルグループを作成
      for (let g = 0; g < particleGroups; g++) {
        createParticleSystem(g)
      }

      animate()
    }

    // パーティクルシステム作成
    const createParticleSystem = (groupIndex: number) => {
      const geometry = new THREE.BufferGeometry()
      const positions: number[] = []
      const colors: number[] = []
      const sizes: number[] = []
      const alphas: number[] = []
      const velocities: number[] = []

      // カラーパレット（未使用のため一時的にコメントアウト）
      /* const colorPalette = [
        new THREE.Color(0x00ffff), // シアン
        new THREE.Color(0xff00ff), // マゼンタ
        new THREE.Color(0xffff00), // イエロー
        new THREE.Color(0x00ff00), // グリーン
        new THREE.Color(0xff0088), // ピンク
        new THREE.Color(0x8800ff), // パープル
      ] */

      for (let i = 0; i < particlesPerGroup; i++) {
        // 上空から落ち始める螺旋配置
        const angle = (i * 0.06) + groupIndex * 0.7
        const r = 2.0 + 1.2 * Math.sin(i * 0.03 + groupIndex)

        positions.push(
          Math.cos(angle) * r + (rand() - 0.5) * 0.4,
          18 + rand() * 6,  // 上からスタート（y=18〜24）
          Math.sin(angle) * r + (rand() - 0.5) * 0.4
        )

        // 初期色：青〜シアン系（HSL使用）
        const shift = 0.55 + 0.05 * Math.sin(i * 0.07 + groupIndex)
        const [r_color, g_color, b_color] = hslToRgb(0.55, 0.9, 0.55 * shift)
        colors.push(r_color, g_color, b_color)

        sizes.push(7.0 + rand() * 3.0)
        alphas.push(0.0)  // 後でフェードイン

        // 落下速度
        velocities.push(
          (rand() - 0.5) * 0.02,
          -0.06 - rand() * 0.02,
          (rand() - 0.5) * 0.02
        )
      }

      geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3))
      geometry.setAttribute('customColor', new THREE.Float32BufferAttribute(colors, 3))
      geometry.setAttribute('size', new THREE.Float32BufferAttribute(sizes, 1))
      geometry.setAttribute('alpha', new THREE.Float32BufferAttribute(alphas, 1))

      const material = new THREE.ShaderMaterial({
        uniforms: {
          uPixelRatio: { value: Math.min(window.devicePixelRatio || 1, 2) },
          uPerspective: { value: 300.0 }
        },
        vertexShader: vertexShader,
        fragmentShader: fragmentShader,
        blending: THREE.AdditiveBlending,
        depthTest: false,
        depthWrite: false,
        transparent: true
      })

      const particleSystem = new THREE.Points(geometry, material)
      ;(particleSystem as any).userData = {
        velocities: velocities,
        originalPositions: [...positions],
        groupIndex: groupIndex
      }

      particles.push(particleSystem)
      scene.add(particleSystem)
    }

    // HSLからRGBへ変換
    const hslToRgb = (h: number, s: number, l: number): [number, number, number] => {
      let r, g, b

      if (s === 0) {
        r = g = b = l
      } else {
        const hue2rgb = (p: number, q: number, t: number) => {
          if (t < 0) t += 1
          if (t > 1) t -= 1
          if (t < 1/6) return p + (q - p) * 6 * t
          if (t < 1/2) return q
          if (t < 2/3) return p + (q - p) * (2/3 - t) * 6
          return p
        }

        const q = l < 0.5 ? l * (1 + s) : l + s - l * s
        const p = 2 * l - q
        r = hue2rgb(p, q, h + 1/3)
        g = hue2rgb(p, q, h)
        b = hue2rgb(p, q, h - 1/3)
      }

      return [r, g, b]
    }

    // イージング関数
    const easeInOutCubic = (t: number): number => {
      return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2
    }

    // アニメーションループ
    const animate = () => {
      animationIdRef.current = requestAnimationFrame(animate)
      timerRef.current++

      let globalYMin = Infinity  // 全パーティクルのY最小値を追跡

      particles.forEach((particleSystem, groupIndex) => {
        const positions = particleSystem.geometry.attributes.position.array as Float32Array
        const colors = particleSystem.geometry.attributes.customColor.array as Float32Array
        const alphas = particleSystem.geometry.attributes.alpha.array as Float32Array
        const sizes = particleSystem.geometry.attributes.size.array as Float32Array
        const velocities = (particleSystem as any).userData.velocities

        switch (effectPhaseRef.current) {
          case 'flowing': {
            // フェーズ1：螺旋を描きつつ自然落下、サイズとアルファは周期で脈動
            for (let i = 0; i < particlesPerGroup; i++) {
              const idx = i * 3

              // 螺旋半径の微小変化
              const angle = timerRef.current * 0.01 + i * 0.01 + groupIndex * 0.2
              const spiralR = Math.sin(timerRef.current * 0.005 + i * 0.002 + groupIndex) * 2.2

              positions[idx] += Math.cos(angle) * spiralR * 0.02 + velocities[idx]
              positions[idx + 1] += velocities[idx + 1]
              positions[idx + 2] += Math.sin(angle) * spiralR * 0.02 + velocities[idx + 2]

              // ゆるい重力加速度
              velocities[idx + 1] -= 0.0008

              // サイズ・アルファ（脈動）と寒色寄りの色移行
              sizes[i] = 6.0 + 2.0 * Math.sin(timerRef.current * 0.05 + i * 0.11 + groupIndex)
              const shift = Math.sin(timerRef.current * 0.01 + i * 0.05 + groupIndex * 0.3) * 0.5 + 0.5
              colors[idx] = colors[idx] * 0.95 + shift * 0.05
              colors[idx + 1] = colors[idx + 1] * 0.95 + (1.0 - shift) * 0.05
              // B成分は保持、彩度は後段のBloomで担保
              alphas[i] = Math.min(1.0, alphas[i] + 0.02) // フェードイン

              // 最小Yの追跡（遷移トリガ）
              if (positions[idx + 1] < globalYMin) globalYMin = positions[idx + 1]
            }

            // 位置 or 経過で自然遷移（元の「positions[1]」参照を全体最小へ改善）
            if (globalYMin <= 14.8 && timerRef.current > 80) {
              effectPhaseRef.current = 'contracting'
              timerRef.current = 0
            }
            break
          }

          case 'contracting': {
            // フェーズ2: 中心に収束
            const progress = Math.min(timerRef.current / 100, 1.0)
            const contractProgress = easeInOutCubic(progress)

            for (let i = 0; i < particlesPerGroup; i++) {
              const idx = i * 3

              // 回転しながら中心に収束する目標位置
              const targetRadius = Math.sin(i * 0.1) * 0.5 * (1 - contractProgress * 0.5)
              const targetAngle = i * 0.1 + timerRef.current * 0.02

              const targetX = Math.cos(targetAngle) * targetRadius
              const targetY = Math.sin(targetAngle) * targetRadius
              const targetZ = Math.cos(targetAngle * 2) * targetRadius * 0.5

              // 徐々に加速しながら目標へ
              const lerpFactor = 0.05 + contractProgress * 0.03
              positions[idx] += (targetX - positions[idx]) * lerpFactor
              positions[idx + 1] += (targetY - positions[idx + 1]) * lerpFactor
              positions[idx + 2] += (targetZ - positions[idx + 2]) * lerpFactor

              // サイズは徐々に小さく
              sizes[i] *= (0.98 - contractProgress * 0.01)

              // 徐々に金色に変化
              const goldIntensity = contractProgress
              colors[idx] = Math.min(1.0, colors[idx] * (1 - goldIntensity * 0.2) + goldIntensity)
              colors[idx + 1] = Math.min(1.0, colors[idx + 1] * (1 - goldIntensity * 0.3) + goldIntensity * 0.85)
              colors[idx + 2] = Math.min(1.0, colors[idx + 2] * (1 - goldIntensity * 0.5))

              // アルファ値を上げて明るく
              alphas[i] = Math.min(1.0, alphas[i] + contractProgress * 0.3)
            }

            // タイマーベースでフェーズ3へ遷移
            if (timerRef.current > 100) {
              console.log('Phase 2 -> 3: Transitioning to bursting phase')
              effectPhaseRef.current = 'bursting'
              setShowImage(true)
              timerRef.current = 0
            }
            break
          }

          case 'bursting': {
            // フェーズ3: 花火状に球面へ拡張（Fibonacci Sphere配置）
            const radius = timerRef.current * 0.32  // 拡張速度

            for (let i = 0; i < particlesPerGroup; i++) {
              const idx = i * 3

              // Fibonacci Sphere配置で均等な球面配置
              const [targetX, targetY, targetZ] = fibonacciSphereTarget(i, particlesPerGroup, radius)

              // 滑らかに目標へ移動
              const lerpFactor = 0.08
              positions[idx] += (targetX - positions[idx]) * lerpFactor
              positions[idx + 1] += (targetY - positions[idx + 1]) * lerpFactor
              positions[idx + 2] += (targetZ - positions[idx + 2]) * lerpFactor

              // 虹色グラデーション（hue回転）
              const hue = (timerRef.current * 0.01 + (i + groupIndex * 37) * 0.002) % 1.0
              const rgb = hslToRgb(hue, 1.0, 0.60)
              colors[idx] = rgb[0]
              colors[idx + 1] = rgb[1]
              colors[idx + 2] = rgb[2]

              // サイズと透明度の変化（フェードアウト）
              sizes[i] = Math.max(0.0, (3.0 - timerRef.current * 0.02) * (1.0 + (i % 7) * 0.03))
              alphas[i] = Math.max(0.0, 1.0 - timerRef.current * 0.005)
            }

            // アニメーション終了判定
            if (timerRef.current > 200) {
              console.log('Phase 3 complete: Animation finished')
              effectPhaseRef.current = 'complete'
              setTimeout(() => {
                onComplete()
              }, 3000)
            }
            break
          }
        }

        // 全体の回転
        particleSystem.rotation.y += 0.0008 * (groupIndex + 1)
        particleSystem.rotation.x += 0.0004 * (groupIndex + 1)

        particleSystem.geometry.attributes.position.needsUpdate = true
        particleSystem.geometry.attributes.customColor.needsUpdate = true
        particleSystem.geometry.attributes.alpha.needsUpdate = true
        particleSystem.geometry.attributes.size.needsUpdate = true
      })

      // ポストプロセシング込みでレンダリング
      composer.render()
    }

    init()

    return () => {
      if (animationIdRef.current) {
        cancelAnimationFrame(animationIdRef.current)
        animationIdRef.current = null
      }
      if (composer) {
        composer.dispose()
      }
      if (rendererRef.current) {
        rendererRef.current.dispose()
        if (mountRef.current && rendererRef.current.domElement.parentNode === mountRef.current) {
          mountRef.current.removeChild(rendererRef.current.domElement)
        }
        rendererRef.current = null
      }
      if (sceneRef.current) {
        sceneRef.current = null
      }
    }
  }, [isVisible, onComplete])

  if (!isVisible) return null

  const imageUrl = customImageUrl || presentImageUrl

  return (
    <div className="fixed inset-0 z-[100] bg-black/90">
      {/* Three.jsエフェクト */}
      <div ref={mountRef} className="absolute inset-0" />

      {/* プレゼント画像表示 */}
      {showImage && imageUrl && (
        <div className="absolute inset-0 flex items-center justify-center animate-fadeIn">
          <div className="relative max-w-lg p-8">
            <img
              src={imageUrl}
              alt={presentName}
              className="w-full h-auto rounded-xl shadow-2xl animate-scaleIn"
            />
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6 rounded-b-xl">
              <h2 className="text-2xl font-bold text-white mb-2">{presentName}</h2>
              {message && (
                <p className="text-white/90 mb-2">{message}</p>
              )}
              <p className="text-white/70 text-sm">From: {grantedByName}</p>
            </div>
          </div>
        </div>
      )}

      {/* スキップボタン */}
      <button
        onClick={onComplete}
        className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
      >
        <span className="text-white text-xl">✕</span>
      </button>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes scaleIn {
          from { transform: scale(0.8); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }

        .animate-fadeIn {
          animation: fadeIn 1s ease-out;
        }

        .animate-scaleIn {
          animation: scaleIn 1s ease-out;
        }
      `}</style>
    </div>
  )
}